/** 同一手机号 / 用户名命中多家公司时, 让用户选一家登录 */
import { Building2, ChevronRight, User } from "lucide-react";
import { Alert, Dialog, Spinner } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { TenantChoice } from "@/types/api";

const ROLE_LABEL: Record<TenantChoice["role"], string> = {
  user: "成员",
  company_admin: "管理员",
  super_admin: "超级管理员",
};

export function TenantPickerModal({
  choices, pendingUserId, error, onPick, onClose,
}: {
  choices: TenantChoice[];
  /** 正在提交的 user_id (显示 loading) */
  pendingUserId: number | null;
  error: string | null;
  onPick: (userId: number) => void;
  onClose: () => void;
}) {
  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title="选择要登录的公司"
      description="该账号属于多家公司，请选择一家继续。"
    >
      <div className="space-y-3">
        <ul className="max-h-80 space-y-2 overflow-auto">
          {choices.map((c) => {
            const Icon = c.company_type === "personal" ? User : Building2;
            return (
              <li key={c.user_id}>
                <button
                  type="button"
                  disabled={pendingUserId !== null}
                  onClick={() => onPick(c.user_id)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg border bg-card px-3.5 py-3 text-left transition-colors",
                    "hover:border-brand/60 hover:bg-primary-soft/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    "disabled:opacity-60",
                    pendingUserId === c.user_id && "border-brand bg-primary-soft/50",
                  )}
                >
                  <span aria-hidden className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {c.company_name ?? "（无公司）"}
                      {c.company_type === "personal" && (
                        <span className="ml-1.5 text-caption font-normal text-muted-foreground">个人</span>
                      )}
                    </span>
                    <span className="block truncate text-caption text-muted-foreground">
                      {c.display_name || c.username} · {ROLE_LABEL[c.role] ?? c.role}
                    </span>
                  </span>
                  {pendingUserId === c.user_id ? (
                    <Spinner className="text-primary" />
                  ) : (
                    <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        {error && <Alert>{error}</Alert>}
      </div>
    </Dialog>
  );
}
